import React from 'react';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { Box, Button, MenuItem, Select, Tab, Tabs, TextField, OutlinedInput, Typography, Divider } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import InputLabel from '@mui/material/InputLabel';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import SaveIcon from '@mui/icons-material/Save';
import moment from 'moment';
import DefaultSelectClient from '../../../components/default-select-client';
import DefaultUploadFile from '../../../components/default-upload-file';
import PurchaseItemDataTable from './components/purchase-item-data-table';
import PurchaseFileDataTable from './components/purchase-file-data-table';
import { usePurchaseCreateEditController } from './context/purchase-create-edit-controller';
import { usePurchaseCreateEditState } from './context/purchase-create-edit-context';
import PageHeader from '../../../components/page-header';

export function PurchaseCreateEditComponent() {
    const {
        handleTabChange,
        handleOnChange,
        handleDateChange,
        setFileUpload,
        setItemsPurchaseJson,
        addOrEditPurchase,
        isSaving
    } = usePurchaseCreateEditController();

    const {
        file,
        tabIndex1,
        purchaseRequest,
        modality,
        disputeMode,
        instrumentSummons,
        legalSupport,
        typeDocument,
        unidEntity,
        purchaseId,
        showValidationErrors,
        itemsPurchaseJson
    } = usePurchaseCreateEditState();

    return (
        <React.Fragment>
            <Grid item xs={12} md={12} lg={12}>
                <PageHeader
                    title={purchaseId ? "Editar Processo de Compra" : "Novo Processo de Compra"}
                    subtitle={purchaseId ? "Atualize as informações do processo de compra" : "Cadastre um novo processo de aquisição ou licitação"}
                    breadcrumbs={[
                        { label: 'Compras e Licitações', href: '/compras-licitacoes' },
                        { label: purchaseId ? 'Editar Processo de Compra' : 'Novo Processo de Compra' }
                    ]}
                />
            </Grid>

            <Grid item xs={12} md={12} lg={12}>
                <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }} elevation={3}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Tabs value={tabIndex1} onChange={(e, newValue) => handleTabChange(e, newValue, 1)}>
                            <Tab label="Dados da Compra/Licitação" />
                            <Tab label="Itens" />
                            {purchaseId !== undefined && <Tab label="Arquivos" />}
                        </Tabs>

                        <Box sx={{ height: tabIndex1 !== 0 ? '600px' : 'auto', display: 'flex', flexDirection: 'column' }}>
                            {tabIndex1 === 0 && (
                                <Grid container spacing={3} sx={{ mt: 1 }}>
                                    {/* Seção: Cliente */}
                                    <Grid item xs={12}>
                                        <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                                            Cliente
                                        </Typography>
                                        <Divider sx={{ mb: 2 }} />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <DefaultSelectClient
                                            required={true}
                                            handleOnChange={handleOnChange}
                                            clientId={purchaseRequest.clientId}
                                        />
                                    </Grid>

                                    {/* Seção: Identificação da Compra/Licitação */}
                                    <Grid item xs={12}>
                                        <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                                            Identificação da Compra/Licitação
                                        </Typography>
                                        <Divider sx={{ mb: 2 }} />
                                    </Grid>

                                    <Grid item xs={12} lg={6} sm={6} xl={6}>
                                        <InputLabel required>Instrumento Convocatório</InputLabel>
                                        <Select
                                            name="select-type-calling-instrument"
                                            fullWidth
                                            value={purchaseRequest.typeCallingInstrumentId || ''}
                                            onChange={handleOnChange}
                                            error={showValidationErrors && (!purchaseRequest.typeCallingInstrumentId || purchaseRequest.typeCallingInstrumentId === 0)}
                                            required
                                        >
                                            {instrumentSummons.map((element) => (
                                                <MenuItem value={Number(element.id)} key={element.id}>
                                                    {element.id} - {element.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>

                                    <Grid item xs={12} lg={6} sm={6} xl={6}>
                                        <InputLabel required>Modalidade</InputLabel>
                                        <Select
                                            name="select-modality"
                                            fullWidth
                                            value={purchaseRequest.modalityId || ''}
                                            onChange={handleOnChange}
                                            error={showValidationErrors && (!purchaseRequest.modalityId || purchaseRequest.modalityId === 0)}
                                            required
                                        >
                                            {modality.map((element) => (
                                                <MenuItem value={Number(element.id)} key={element.id}>
                                                    {element.id} - {element.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>

                                    <Grid item xs={12} lg={4} sm={4} xl={4}>
                                        <InputLabel required>Modo de Disputa</InputLabel>
                                        <Select
                                            name="select-dispute-mode"
                                            fullWidth
                                            value={purchaseRequest.disputeModeId || ''}
                                            onChange={handleOnChange}
                                            error={showValidationErrors && (!purchaseRequest.disputeModeId || purchaseRequest.disputeModeId === 0)}
                                            required
                                        >
                                            {disputeMode.map((element) => (
                                                <MenuItem value={Number(element.id)} key={element.id}>
                                                    {element.id} - {element.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>

                                    <Grid item xs={12} lg={4} sm={4} xl={4}>
                                        <InputLabel required>Amparo Legal</InputLabel>
                                        <Select
                                            name="select-legal-support"
                                            fullWidth
                                            value={purchaseRequest.legalSupportId || ''}
                                            onChange={handleOnChange}
                                            error={showValidationErrors && (!purchaseRequest.legalSupportId || purchaseRequest.legalSupportId === 0)}
                                            required
                                        >
                                            {legalSupport.map((element) => (
                                                <MenuItem value={Number(element.id)} key={element.id}>
                                                    {element.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>

                                    <Grid item xs={12} lg={4} sm={6} xl={4}>
                                        <InputLabel required>Unidade Compradora</InputLabel>
                                        <Select
                                            name="select-cod-buyer-unit"
                                            fullWidth
                                            value={purchaseRequest.codBuyerUnit || ''}
                                            onChange={handleOnChange}
                                            error={showValidationErrors && (!purchaseRequest.codBuyerUnit || purchaseRequest.codBuyerUnit.length === 0)}
                                            required
                                        >
                                            {unidEntity.map((element) => (
                                                <MenuItem value={element.unitCode} key={element.unitCode}>
                                                    {element.unitCode} - {element.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>

                                    <Grid item xs={12} lg={2} sm={3} xl={2}>
                                        <InputLabel required>Ano Orçamentário</InputLabel>
                                        <OutlinedInput
                                            name="input-year-purchase"
                                            type="number"
                                            fullWidth
                                            autoComplete='off'
                                            value={purchaseRequest.yearPurchase || ''}
                                            onChange={handleOnChange}
                                            error={showValidationErrors && (!purchaseRequest.yearPurchase || purchaseRequest.yearPurchase === 0)}
                                            required
                                        />
                                    </Grid>

                                    <Grid item xs={12} lg={2} sm={3} xl={2}>
                                        <InputLabel required>Número</InputLabel>
                                        <OutlinedInput
                                            name="input-number"
                                            fullWidth
                                            autoComplete='off'
                                            value={purchaseRequest.numberPurchase || ''}
                                            onChange={handleOnChange}
                                            error={showValidationErrors && (!purchaseRequest.numberPurchase || purchaseRequest.numberPurchase.length === 0)}
                                            required
                                        />
                                    </Grid>

                                    <Grid item xs={12} lg={2} sm={3} xl={2}>
                                        <InputLabel>Número Processo</InputLabel>
                                        <OutlinedInput
                                            name="input-process-number"
                                            fullWidth
                                            autoComplete='off'
                                            value={purchaseRequest.processNumber || ''}
                                            onChange={handleOnChange}
                                        />
                                    </Grid>

                                    {/* Seção: Datas */}
                                    <Grid item xs={12}>
                                        <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                                            Datas
                                        </Typography>
                                        <Divider sx={{ mb: 2 }} />
                                    </Grid>

                                    <Grid item xs={12} lg={3} sm={6} xl={3}>
                                        <InputLabel>Data de Abertura de Propostas</InputLabel>
                                        <DesktopDatePicker
                                            inputFormat="DD/MM/YYYY"
                                            renderInput={(params) => <TextField {...params} fullWidth autoComplete='off' />}
                                            value={purchaseRequest.proposalOpeningDate !== undefined ? moment(purchaseRequest.proposalOpeningDate) : null}
                                            onChange={(newValue) => {
                                                handleDateChange(newValue, 'proposalOpeningDate');
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} lg={3} sm={6} xl={3}>
                                        <InputLabel>Data de Encerramento de Propostas</InputLabel>
                                        <DesktopDatePicker
                                            inputFormat="DD/MM/YYYY"
                                            renderInput={(params) => <TextField {...params} fullWidth autoComplete='off' />}
                                            value={purchaseRequest.proposalCloseDate !== undefined ? moment(purchaseRequest.proposalCloseDate) : null}
                                            onChange={(newValue) => {
                                                handleDateChange(newValue, 'proposalCloseDate');
                                            }}
                                        />
                                    </Grid>

                                    {/* Seção: Descrição */}
                                    <Grid item xs={12}>
                                        <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                                            Descrição
                                        </Typography>
                                        <Divider sx={{ mb: 2 }} />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <InputLabel required={purchaseId === undefined}>Título</InputLabel>
                                        <OutlinedInput
                                            name="input-title"
                                            fullWidth
                                            autoComplete='off'
                                            value={purchaseRequest.title || ''}
                                            onChange={handleOnChange}
                                            disabled={purchaseId !== undefined}
                                            error={showValidationErrors && purchaseId === undefined && (!purchaseRequest.title || purchaseRequest.title.length === 0)}
                                            required={purchaseId === undefined}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <InputLabel required>Objeto</InputLabel>
                                        <OutlinedInput
                                            name="input-object"
                                            fullWidth
                                            autoComplete='off'
                                            multiline
                                            rows={4}
                                            value={purchaseRequest.objectPurchase || ''}
                                            onChange={handleOnChange}
                                            error={showValidationErrors && (!purchaseRequest.objectPurchase || purchaseRequest.objectPurchase.length === 0)}
                                            required
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <InputLabel>Informações Complementares</InputLabel>
                                        <OutlinedInput
                                            name="input-information-complementary"
                                            fullWidth
                                            autoComplete='off'
                                            multiline
                                            rows={3}
                                            value={purchaseRequest.informationComplementary || ''}
                                            onChange={handleOnChange}
                                        />
                                    </Grid>

                                    {purchaseId === undefined && (
                                        <>
                                            {/* Seção: Documento */}
                                            <Grid item xs={12}>
                                                <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'primary.main', fontWeight: 600 }}>
                                                    Documento
                                                </Typography>
                                                <Divider sx={{ mb: 2 }} />
                                            </Grid>

                                            <Grid item xs={12} lg={4} sm={6} xl={4}>
                                                <InputLabel required sx={{ mb: 1 }}>Tipo de Documento</InputLabel>
                                                <Select
                                                    name="select-type-document"
                                                    fullWidth
                                                    value={purchaseRequest.typeDocumentId || ''}
                                                    onChange={handleOnChange}
                                                    error={showValidationErrors && (!purchaseRequest.typeDocumentId || purchaseRequest.typeDocumentId === 0)}
                                                    required
                                                >
                                                    {typeDocument.map((element) => (
                                                        <MenuItem value={Number(element.id)} key={element.id}>
                                                            {element.id} - {element.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </Grid>

                                            <Grid item xs={12} lg={8} sm={12} xl={8}>
                                                <InputLabel required sx={{ mb: 1 }}>Arquivo</InputLabel>
                                                <DefaultUploadFile
                                                    props={{
                                                        id: "file-upload-purchase",
                                                        setFileUpload,
                                                        file,
                                                        extensions: ["pdf"],
                                                        maxFileSizeMb: 30,
                                                        error: showValidationErrors && (file === undefined || file === null)
                                                    }}
                                                />
                                            </Grid>
                                        </>
                                    )}
                                </Grid>
                            )}

                            {tabIndex1 === 1 && (
                                <PurchaseItemDataTable
                                    props={{
                                        purchaseRequest: purchaseRequest,
                                        purchaseId: purchaseId?.toString(),
                                        setItemsPurchaseJson: setItemsPurchaseJson,
                                        itemsPurchaseJson: itemsPurchaseJson
                                    }}
                                />
                            )}

                            {tabIndex1 === 2 && (
                                <PurchaseFileDataTable
                                    props={{
                                        purchaseRequest: purchaseRequest,
                                        purchaseId: purchaseId?.toString(),
                                    }}
                                />
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2, mt: 2, borderTop: '1px solid #e0e0e0' }}>
                            <Box sx={{ flex: '1 1 auto' }} />
                            <LoadingButton
                                variant="contained"
                                onClick={addOrEditPurchase}
                                endIcon={<SaveIcon />}
                                loading={isSaving}
                                disabled={isSaving}
                            >
                                Salvar
                            </LoadingButton>
                        </Box>
                    </LocalizationProvider>
                </Paper>
            </Grid>
        </React.Fragment>
    );
}
